import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { Scanner } from "@yudiel/react-qr-scanner";
import { useNavigate } from "react-router-dom";
import styles from "./ModalAddProspectCompany.module.sass";
import Icon from "../Icon";
import Card from "../Card";
import TooltipGlodal from "../TooltipGlodal";
import { useAuth } from "../../contexts/AuthContext";
import { checkSessionCode, checkSessionQr } from "../../services/sessionCode.service";
import { showSuccess, showError } from "../../services/toast.service";

const ModalAttendance = ({ visible, onClose, attendance }) => {
  const modalRef = useRef(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [codeDigits, setCodeDigits] = useState(["", "", "", ""]);
  const [isScanning, setIsScanning] = useState(false);
  const [qrError, setQrError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef([]);

  // Génère le titre de la modal basé sur les données de l'attendance
  const getModalTitle = () => {
    if (!attendance) {
      return "Pointage de présence";
    }

    // Essayer d'extraire le titre de la session depuis les données expandées
    const session = attendance.expand?.session_id;
    const formation = session?.expand?.formation_id;
    
    if (formation?.title) {
      return `Pointage - ${formation.title}`;
    } else if (session?.title) {
      return `Pointage - ${session.title}`;
    } else {
      return `Pointage de présence`;
    }
  };

  // Gère le défilement du corps
  useEffect(() => {
    const targetElement = modalRef.current || document.querySelector("#modal-attendance");

    if (visible) {
      if (targetElement) {
        disableBodyScroll(targetElement);
      }
    } else {
      clearAllBodyScrollLocks();
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [visible]);

  // Gère la touche 'Échap' pour fermer la modale
  const escFunction = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction);
    return () => {
      document.removeEventListener("keydown", escFunction);
    };
  }, [escFunction]);

  // Gestion de la saisie des chiffres
  const handleDigitChange = (index, value) => {
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newDigits = [...codeDigits];
      newDigits[index] = value;
      setCodeDigits(newDigits);
      
      // Passer au champ suivant si un chiffre est saisi
      if (value && index < 3) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  // Gestion de la touche backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Gestion de la soumission du code
  const handleCodeSubmit = async () => {
    const fullCode = codeDigits.join('');
    if (fullCode.length !== 4) {
      showError("Veuillez saisir un code à 4 chiffres");
      return;
    }

    if (!currentUser?.id) {
      showError("Utilisateur non connecté");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await checkSessionCode(fullCode, currentUser.id, attendance);
      
      if (result.success) {
        showSuccess(result.data.message || "Code valide ! Redirection en cours...");
        onClose();
        
        // Rediriger vers la page de signature avec l'ID de l'attendance
        navigate(`/signature/${result.data.attendanceId}`);
      } else {
        // Gestion des différents codes d'erreur retournés par le backend
        switch (result.code) {
          case 'INVALID_CODE':
            showError(result.error || "Le code saisi ne correspond à aucune session. Veuillez vérifier et réessayer.");
            break;
          case 'NOT_IN_GROUP':
            showError(result.error || "Vous n'appartenez pas à cette session.");
            break;
          case 'ALREADY_SIGNED':
            showSuccess(result.error || "Vous avez déjà signé votre présence pour cette session.");
            // Fermer la modal après 2 secondes pour laisser le temps de lire le message
            setTimeout(() => {
              onClose();
            }, 2000);
            break;
          case 'SESSION_NOT_FOUND':
            showError(result.error || "Session introuvable. Veuillez vérifier le code saisi.");
            break;
          case 'SESSION_CLOSED':
            showError(result.error || "Cette session n'est pas ouverte aux inscriptions.");
            break;
          case 'SESSION_ENDED':
            showError(result.error || "Cette session est déjà terminée.");
            break;
          case 'SESSION_NOT_STARTED':
            showError(result.error || "Cette session n'est pas encore accessible.");
            break;
          case 'SESSION_ACCESS_EXPIRED':
            showError(result.error || "L'accès à cette session n'est plus autorisé.");
            break;
          case 'UNAUTHORIZED_PARTNER':
            showError(result.error || "Vous n'êtes pas autorisé à accéder à cette session.");
            break;
          case 'STUDENT_NOT_FOUND':
            showError(result.error || "Votre profil étudiant n'a pas pu être trouvé.");
            break;
          case 'NO_GROUP_FOR_SESSION':
            showError(result.error || "Aucun groupe d'étudiants n'est lié à cette session.");
            break;
          case 'PARTNER_MISMATCH':
            showError(result.error || "Une erreur de configuration a été détectée pour cette session.");
            break;
          case 'SERVER_ERROR':
          default:
            showError(result.error || "Une erreur technique s'est produite. Veuillez réessayer dans quelques instants.");
            break;
        }
      }
    } catch (error) {
      console.error("Erreur lors de la vérification du code:", error);
      showError("Une erreur inattendue s'est produite. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gestion du scan QR
  const handleQRScan = () => {
    setIsScanning(true);
    setQrError(null);
    console.log("Démarrage du scan QR");
  };

  // Gestion du résultat du scan QR
  const handleQRResult = async (result) => {
    if (result && result.length > 0) {
      const qrData = result[0].rawValue;
      console.log("QR Code scanné:", qrData);
      
      // Extraire le token de l'URL scannée
      // Format attendu: localhost/attendance/987487165465/17e7441e636043a4f54d68cf3f349ba1
      const urlPattern = /\/attendance\/([^\/]+)\/([^\/]+)$/;
      const match = qrData.match(urlPattern);
      
      if (match) {
        const sessionId = match[1];
        const qrToken = match[2];
        
        console.log("Session ID extraite:", sessionId);
        console.log("Token QR extrait:", qrToken);
        
        setIsScanning(false);
        
        try {
          // Appeler le backend avec le token QR
          const result = await checkSessionQr(qrToken, currentUser?.id, attendance);
          
          if (result.success) {
            showSuccess("QR Code valide ! Redirection vers la signature...");
            onClose();
            // Rediriger vers la page de signature
            navigate(`/signature/${result.data.attendanceId}`);
          } else {
            showError(result.error || "Erreur lors du traitement du QR Code");
          }
        } catch (error) {
          console.error("Erreur lors du traitement du QR Code:", error);
          showError("Erreur lors du traitement du QR Code");
        }
      } else {
        console.error("Format de QR Code non reconnu:", qrData);
        showError("Format de QR Code non reconnu");
        setIsScanning(false);
      }
    }
  };

  // Gestion des erreurs du scan QR
  const handleQRError = (error) => {
    console.error("Erreur du scanner QR:", error);
    setQrError("Erreur d'accès à la caméra. Vérifiez les permissions.");
  };

  // Arrêter le scan QR
  const handleStopScan = () => {
    setIsScanning(false);
    setQrError(null);
  };

  // Ne rend le portail que si la modale est visible
  if (!visible) {
    return null;
  }

  return createPortal(
    <>
      <div id="modal-attendance" className={styles.modal} ref={modalRef}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose} aria-label="Fermer la modale">
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer}>
          <Card
            className={styles.card}
            title={getModalTitle()}
            classTitle="title-blue"
          >
            <div className={styles.cardsContainer}>
              {/* Carte Code */}
              <div className={styles.codeCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.iconWrapper}>
                    <Icon name="activity" size="32" />
                  </div>
                  <h3>Saisir un code</h3>
                </div>
                <p className={styles.cardDescription}>Entrez votre code d'accès à 4 chiffres</p>
                
                <div className={styles.codeInputs}>
                  {codeDigits.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => inputRefs.current[index] = el}
                      type="text"
                      value={digit}
                      onChange={(e) => handleDigitChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className={styles.digitInput}
                      maxLength="1"
                      inputMode="numeric"
                    />
                  ))}
                </div>
                
                <button 
                  onClick={handleCodeSubmit}
                  className={`${styles.submitButton} button`}
                  disabled={codeDigits.join('').length !== 4 || isSubmitting}
                >
                  {isSubmitting ? "Vérification..." : "Valider le code"}
                </button>
              </div>

              {/* Carte QR Code */}
              <div className={styles.qrCard} onClick={!isScanning ? handleQRScan : undefined}>
                <div className={styles.cardHeader}>
                  <div className={styles.iconWrapper}>
                    <Icon name="qr-code" size="32" />
                  </div>
                  <h3>Scanner QR Code</h3>
                </div>
                <p className={styles.cardDescription}>
                  {isScanning ? "Scan en cours..." : "Cliquez pour ouvrir la caméra"}
                </p>
                
                <div className={styles.qrArea}>
                  {isScanning ? (
                    <div className={styles.scanningArea}>
                      {qrError ? (
                        <div className={styles.errorMessage}>
                          <Icon name="close" size="32" />
                          <p>{qrError}</p>
                          <button 
                            onClick={handleStopScan}
                            className={`${styles.button} button`}
                          >
                            Réessayer
                          </button>
                        </div>
                      ) : (
                        <div className={styles.qrScanner}>
                          <Scanner
                            onScan={handleQRResult}
                            onError={handleQRError}
                            constraints={{
                              video: {
                                width: { ideal: 400 },
                                height: { ideal: 300 },
                                facingMode: 'environment'
                              }
                            }}
                            styles={{
                              container: { width: '100%', height: '250px' }
                            }}
                          />
                          <div className={styles.scanOverlay}>
                            <div className={styles.scanFrame}></div>
                            <p>Positionnez le QR code dans le cadre</p>
                          </div>
                          <button 
                            onClick={handleStopScan}
                            className={`${styles.stopButton} button`}
                          >
                            Arrêter le scan
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.qrPlaceholder}>
                      <Icon name="qr-code" size="64" />
                      <p>Cliquez pour scanner</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
          <TooltipGlodal />
        </div>
      </div>
    </>,
    document.body
  );
};

export default ModalAttendance;